import React, { useEffect, useRef, useState } from "react";

import { usePrint } from "./PrintContext";

import SurveyFormHeader from "./SurveyFormHeader";
import SurveyFormDivider from "./SurveyFormDivider";
import SurveyFormCsdNumber from "./SurveyFormCsdNumber";
import FormItem from "./FormItem";
import SurveyFormTotals from "./SurveyFormTotals";
import SurveyFormUnitCsdFees from "./SurveyFormUnitCsdFees";

const SurveyForm = ({ title = "Survey Form", data = {} }) => {
  const [formState, setFormState] = useState(() => resetFormState());
  const [showModal, setShowModal] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const csdNumberRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    csdNumberRef.current?.focus();
    document.title = title;

    // Create ResizeObserver instance
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        setContainerWidth(width);
      }
    });

    // Start observing the container element
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Clean up observer on component unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, [title]);

  function resetFormState() {
    return {
      items: data.items.map((item, index, array) =>
        makeItem(item, index, array)
      ),
      csdNumber: "",
    };
  }

  const handleReset = () => {
    setFormState(resetFormState());
    csdNumberRef.current?.focus();
  };

  const handlePrint = usePrint();

  function makeItem(item, index, array) {
    const model = {
      id: item.id,
      name: item.name,
      options: item.options,
      selectedOption: null,
      price: item.price,
      planTypes: item.planTypes,
      selectedPlanType: null,
      maxQuantity: item.maxQuantity || 100,
      quantity: 0,
      total: 0,
      isEnabled: false,
      isSelected: function () {
        if (this.selectedOption === null) return false;

        if (!this.planTypes) return true;

        return this.selectedPlanType !== null;
      },
      getPrice: function () {
        return this.selectedOption
          ? this.selectedOption.price
          : this.price || 0;
      },
    };

    model.quantity = data.getDepositFeeQuantity(
      model.selectedOption,
      model.selectedPlanType
    );
    model.isEnabled = data.selectedOption && item.id !== data.depositFeesId;

    return model;
  }

  const handleCsdNumberChange = (value) => {
    setFormState((prevState) => ({
      ...prevState,
      csdNumber: value,
    }));
  };

  const handleOptionChange = (itemId, optionId) => {
    let optionsItem;

    setFormState((prevState) => ({
      ...prevState,
      items: prevState.items.map((item) => {
        if (item.id === itemId) {
          const selectedOption = item.options?.find(
            (opt) => opt.id === optionId
          );

          let selectedPlanType = item.selectedPlanType;

          if (item.planTypes) {
            if (selectedOption.defaultPlanTypeId) {
              selectedPlanType = item.planTypes.find(
                (opt) => opt.id === selectedOption.defaultPlanTypeId
              );
            } else {
              selectedPlanType = null;
            }
          }

          const itemPrice = selectedOption
            ? selectedOption.price
            : item.price || 0;
          const total = itemPrice * (item.quantity || 0);

          optionsItem = {
            ...item,
            total,
            selectedOption,
            selectedPlanType,
          };

          return optionsItem;
        }
        if (item.id === data.depositFeesId) {
          const quantity = data.getDepositFeeQuantity(
            optionsItem.selectedOption,
            optionsItem.selectedPlanType
          );
          const total = item.getPrice() * quantity;

          return {
            ...item,
            quantity,
            total,
          };
        }
        return item;
      }),
    }));
  };

  const handlePlanTypeChange = (itemId, planTypeId) => {
    let optionsItem;

    setFormState((prevState) => ({
      ...prevState,
      items: prevState.items.map((item) => {
        if (item.id === itemId) {
          const selectedPlanType = item.planTypes.find(
            (planType) => planType.id === planTypeId
          );

          optionsItem = {
            ...item,
            selectedPlanType,
          };

          return optionsItem;
        }
        if (item.id === data.depositFeesId) {
          const quantity = data.getDepositFeeQuantity(
            optionsItem,
            optionsItem.selectedPlanType
          );
          const total = item.getPrice() * quantity;

          return {
            ...item,
            quantity,
            total,
          };
        }

        return item;
      }),
    }));
  };

  const handleQuantityChange = (itemId, value) => {
    const item = formState.items.find((item) => item.id === itemId);

    const newValue = Math.min(
      Math.max(0, parseInt(value) || 0),
      item.maxQuantity
    );
    const newTotal = item.getPrice() * (newValue || 0);

    setFormState((prevState) => ({
      ...prevState,
      items: prevState.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              quantity: newValue,
              total: newTotal,
            }
          : item
      ),
    }));
  };

  const calculateTotals = () => {
    return formState.items.reduce(
      (acc, item) => {
        const quantity = item.quantity || 0;
        const total = item.total || 0;
        return {
          totalQuantity: acc.totalQuantity + quantity,
          totalPrice: acc.totalPrice + total,
        };
      },
      { totalQuantity: 0, totalPrice: 0 }
    );
  };

  const firstItem = formState.items[0] || {};
  const isOptionSelected = firstItem.isSelected();
  const { totalPrice } = calculateTotals();

  return (
    <div ref={containerRef}>
      <SurveyFormHeader
        title={data.title}
        onReset={handleReset}
        onPrint={handlePrint}
        isOptionSelected={isOptionSelected}
      />

      <SurveyFormDivider height={2} />

      <SurveyFormCsdNumber
        csdNumber={formState.csdNumber}
        csdNumberRef={csdNumberRef}
        onCsdNumberChange={handleCsdNumberChange}
      />

      <SurveyFormDivider height={1} />

      {formState.items.map((item) => (
        <FormItem
          key={item.id}
          item={item}
          isOptionSelected={isOptionSelected}
          depositFeesId={data.depositFeesId}
          onOptionChange={handleOptionChange}
          onPlanTypeChange={handlePlanTypeChange}
          onQuantityChange={handleQuantityChange}
        />
      ))}

      <SurveyFormTotals
        totalPrice={totalPrice}
        isInfoOnFeesAvailable={data.enableModal}
        onModalClick={() => setShowModal(true)}
      />

      <SurveyFormDivider />

      <SurveyFormUnitCsdFees
        isInfoOnFeesAvailable={data.enableModal}
        showModal={showModal}
        width={containerWidth}
        onModalClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default SurveyForm;
